import type { ReactNode } from 'react';
import { shopLabel } from './shopname';
import { tillBuild } from './build';
import { fa } from './fa';

/*
 * The frame and the top bar.
 *
 * The design was drawn at 1280×800, an Android tablet in landscape, and the
 * rule it encodes is that the catalogue, the basket and the total are all on
 * screen at once: a cashier who has to scroll to see the total is a cashier who
 * reads it out wrong. That rule is about what fits, not about pixel counts.
 *
 * The frame used to hold 1280×800 literally and centre itself. On the till
 * boards — 1920×1080 and up — that left the whole app a small card floating in
 * the middle of a maximised window, which reads as a broken app rather than a
 * deliberate one. It now fills the viewport, and the screens inside are already
 * fluid (flex with minWidth/minHeight 0, 1fr grids), so nothing reflows badly;
 * only the cart keeps its fixed 428px, which is what makes the totals column
 * land in the same place on every board.
 */

export type NetState = 'online' | 'offline' | 'syncing';

/**
 * The network chip.
 *
 * Offline is deliberately not red. The shop is still selling — every sale is
 * recorded, numbered and queued — so a red alarm would be a lie that makes the
 * cashier stop and fetch someone. Orange, the brand's warning, says "noted, and
 * handled". The dot breathes rather than flashes for the same reason.
 */
function NetworkChip({ state, queued, refused }: { state: NetState; queued: number; refused: number }) {
  const spec = {
    online: {
      bg: 'var(--luma-tint-success-bg)', border: 'var(--luma-tint-success-border)',
      text: 'var(--luma-tint-success-text)', dot: 'var(--luma-success)',
      label: 'آنلاین — همه‌چیز همگام', pulse: false,
    },
    offline: {
      bg: 'var(--luma-tint-warning-bg)', border: 'var(--luma-tint-warning-border)',
      text: 'var(--luma-tint-warning-text)', dot: 'var(--luma-accent)',
      label: 'آفلاین — فروش ادامه دارد', pulse: true,
    },
    syncing: {
      bg: 'var(--luma-tint-info-bg)', border: 'var(--luma-tint-info-border)',
      text: 'var(--luma-tint-info-text)', dot: 'var(--luma-info)',
      label: 'در حال همگام‌سازی…', pulse: true,
    },
  }[state];

  return (
    <>
      <div
        style={{
          height: 34, padding: '0 12px', borderRadius: 999,
          background: spec.bg, border: `1px solid ${spec.border}`,
          display: 'flex', alignItems: 'center', gap: 7,
          fontSize: 13, fontWeight: 600, color: spec.text,
        }}
      >
        <span
          style={{
            width: 8, height: 8, borderRadius: '50%', background: spec.dot,
            animation: spec.pulse ? 'lumaPulse 1.6s ease-in-out infinite' : undefined,
          }}
        />
        {spec.label}
      </div>

      {/* The queue is its own chip, because "offline" and "three sales still to
          send" are different facts and the cashier acts on the second one. */}
      {queued > 0 && (
        <div
          style={{
            height: 34, padding: '0 11px', borderRadius: 999,
            background: 'var(--luma-surface-hover)', border: '1px solid var(--luma-line-strong)',
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 13, fontWeight: 600, color: 'var(--luma-ink-2)',
          }}
        >
          {state === 'syncing'
            ? `همگام‌سازی ${fa(queued)} فاکتور…`
            : `${fa(queued)} فاکتور در صف همگام‌سازی`}
        </div>
      )}

      {/*
        Refused events, and this one IS red.
        Everything else in this header is deliberately not: offline is orange
        because the shop is still selling and every sale is recorded, numbered
        and queued, so an alarm would be a lie. This is the opposite case. The
        server has refused these permanently; no amount of waiting or
        reconnecting will deliver them, and the queue chip will never show
        them because a refused event is not owed.

        That is exactly how it went unseen: a till reported آنلاین — همه‌چیز
        همگام with nothing queued, for a day, while every event it wrote was
        being rejected. The green chip was telling the truth about the network
        and a lie about the shop's books. This chip is the only thing on the
        screen that can contradict it, so it does not get to be gentle.
      */}
      {refused > 0 && (
        <div
          style={{
            height: 34, padding: '0 11px', borderRadius: 999,
            background: 'var(--luma-tint-danger-bg)',
            border: '1px solid var(--luma-tint-danger-border)',
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 13, fontWeight: 700, color: 'var(--luma-tint-danger-text)',
          }}
        >
          {`${fa(refused)} رویداد رد شده — به پشتیبانی اطلاع دهید`}
        </div>
      )}
    </>
  );
}

export function Shell({
  shopName, branchName, cashierName, cashierInitials, shiftSince,
  net, queued, refused = 0, agentBuild, onBranch, menu, children,
}: {
  /**
   * The shop's own name. Optional: a device that has not yet replicated its
   * merchant identity does not know it, and the header falls back to the
   * product's name rather than showing an empty space where a shop should be.
   */
  shopName?: string;
  branchName: string;
  cashierName: string;
  cashierInitials: string;
  shiftSince: string | null;
  net: NetState;
  queued: number;
  /**
   * Events the cloud has permanently refused.
   *
   * Separate from `queued` because they are not waiting for anything. A
   * refused event is excluded from the sync queue — correctly, since retrying
   * it for ever would wedge every later event — which is precisely why the
   * network chip can read آنلاین — همه‌چیز همگام while nothing at all is
   * reaching the books. Anything above zero needs a person.
   */
  refused?: number;
  /**
   * What build the local agent reports on /v1/status.
   *
   * Shown beside the till's own build because they are installed together and
   * fail apart: the bundle is files on disk the wrapper serves, the agent is a
   * separate process the wrapper supervises, and an install that replaced one
   * and not the other leaves a screen talking to a service that does not have
   * the endpoint it is calling. Two stamps on screen make that five seconds of
   * looking instead of an afternoon.
   *
   * Optional, and absent is a fact worth showing rather than hiding: the till
   * polls /status every thirty seconds, so a blank here means the agent has
   * not answered yet or is not answering at all.
   */
  agentBuild?: string;
  onBranch?: () => void;
  menu?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      dir="rtl"
      style={{
        height: '100vh',
        display: 'flex', flexDirection: 'column',
        background: 'var(--luma-surface-desk)',
      }}
    >
      <div
        style={{
          flex: 1, minHeight: 0, width: '100%', background: 'var(--luma-surface)',
          overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          position: 'relative',
        }}
      >
        <header
          style={{
            height: 56, flex: 'none', background: 'var(--luma-card)',
            borderBottom: '1px solid var(--luma-line)',
            display: 'flex', alignItems: 'center', gap: 12, padding: '0 14px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* The real mark, not a letter standing in for one. Served from the
                bundle root, which is a real origin under the wrapper's virtual
                host — the same path works in the browser build. */}
            <img
              src="logo.png"
              alt=""
              width={30}
              height={30}
              style={{ borderRadius: 9, display: 'block' }}
            />
            {/*
              * The SHOP's name, not the product's.
              *
              * This said "لوما استور" on every till in every shop — the name of
              * the software, which the person standing at the counter already
              * knows and cannot use. A cashier with two branches open on two
              * screens needs to see whose counter this is; the vendor's name
              * tells them nothing. The mark to the left still carries the
              * product identity.
              *
              * Falls back when the device has not yet learned its merchant
              * identity, so a freshly paired till reads as the product rather
              * than as a shop with no name.
              */}
            <div style={{ lineHeight: 1.2 }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>
                {shopLabel(shopName, 'لوما استور')}
              </div>
              {/*
                * The two build stamps, under the shop's name.
                *
                * Here rather than on a settings screen because the thing they
                * are for is watching a version change: after an install, or
                * after an update is meant to have landed on its own, somebody
                * needs to see which build is running without navigating
                * anywhere or being talked through a menu over the phone.
                *
                * Deliberately tiny and muted — it is for the rare day someone
                * is looking for it, and it must cost the sale screen nothing on
                * every other day.
                *
                * LTR, and pos-stamp for the font. These are git describes, and
                * they get compared against a release character by character,
                * which the body face actively prevents: its FD cut redraws
                * ASCII digits as Persian ones, turning the hash 5809dd7 into
                * 5809ddV on screen. See .pos-stamp in tokens.css.
                */}
              <div
                dir="ltr"
                className="pos-stamp"
                title="نسخهٔ صندوق و نسخهٔ سرویس محلی"
                style={{
                  fontSize: 10, color: 'var(--luma-placeholder)', fontWeight: 500,
                  textAlign: 'right',
                }}
              >
                {tillBuild} · {agentBuild || '—'}
              </div>
            </div>
          </div>

          <div style={{ width: 1, height: 24, background: 'var(--luma-line)' }} />

          <button
            onClick={onBranch}
            style={{
              height: 44, padding: '0 12px', borderRadius: 9,
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 13, fontWeight: 600, color: 'var(--pos-ink-dark)',
            }}
          >
            <span style={{ color: 'var(--luma-muted)', fontWeight: 500 }}>شعبه</span>
            {branchName}
            <span style={{ color: 'var(--luma-placeholder)', fontSize: 11 }}>▾</span>
          </button>

          <div style={{ flex: 1 }} />

          <NetworkChip state={net} queued={queued} refused={refused} />

          <div style={{ width: 1, height: 24, background: 'var(--luma-line)' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <div
              style={{
                width: 30, height: 30, borderRadius: '50%', background: '#EFE7DF',
                border: '1px solid #E0D5C9', display: 'grid', placeItems: 'center',
                fontWeight: 700, fontSize: 12, color: '#7A6A5C',
              }}
            >
              {cashierInitials}
            </div>
            <div style={{ lineHeight: 1.25 }}>
              <div style={{ fontWeight: 700 }}>{cashierName}</div>
              <div style={{ fontSize: 11, color: 'var(--luma-muted)' }}>
                {shiftSince ? `شیفت باز · از ${shiftSince}` : 'شیفت بسته'}
              </div>
            </div>
          </div>

          {menu}
        </header>

        {children}
      </div>
    </div>
  );
}
